import { useAuthStore } from '@/stores/auth.ts';
import * as Amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { featureFlagService } from '@/models/feature-flag';
export function planState(tag, isTrialUser) {
    if (!tag) {
        return undefined;
    }
    if (tag === 'free') {
        return 'free';
    }
    return isTrialUser ? 'trial' : 'paid';
}
export class AmplitudeService {
    initAmplitude() {
        var _a, _b, _c, _d;
        if (!this.canUseAmplitude()) {
            return;
        }
        const anonymousDeviceId = localStorage.getItem('amp_annon_id');
        Amplitude.init(process.env.AMPLITUDE_API_KEY, (_b = (_a = this.getAuthStore().user) === null || _a === void 0 ? void 0 : _a.profile) === null || _b === void 0 ? void 0 : _b.id, {
            deviceId: anonymousDeviceId || undefined,
            serverZone: 'EU',
            autocapture: {
                attribution: true,
                pageViews: false,
                sessions: true,
                formInteractions: false,
                fileDownloads: false,
                elementInteractions: false,
            },
        });
        if (anonymousDeviceId) {
            localStorage.removeItem('amp_annon_id');
        }
        Amplitude.setUserId((_d = (_c = this.getAuthStore().user) === null || _c === void 0 ? void 0 : _c.profile) === null || _d === void 0 ? void 0 : _d.id);
    }
    initAnonymousAmplitude(deviceId = null) {
        if (!process.env.AMPLITUDE_API_KEY) {
            return;
        }
        if (!deviceId) {
            return;
        }
        Amplitude.init(process.env.AMPLITUDE_API_KEY, undefined, {
            deviceId: deviceId,
            serverZone: 'EU',
            autocapture: {
                attribution: true,
                pageViews: false,
                sessions: false,
                formInteractions: false,
                fileDownloads: false,
                elementInteractions: false,
            },
        });
        localStorage.setItem('amp_annon_id', deviceId || '');
    }
    resetAmplitude() {
        Amplitude.flush();
        Amplitude.reset();
    }
    trackEvent(eventName, eventProperties = {}) {
        var _a, _b;
        if (!this.canUseAmplitude()) {
            return;
        }
        const userSubscription = (_b = (_a = this.getAuthStore().user) === null || _a === void 0 ? void 0 : _a.permissions) === null || _b === void 0 ? void 0 : _b.user_subscription;
        eventProperties = Object.assign(Object.assign({}, (eventProperties !== null && eventProperties !== void 0 ? eventProperties : {})), { trial_user: userSubscription === null || userSubscription === void 0 ? void 0 : userSubscription.is_trial_user, user_subscription: userSubscription === null || userSubscription === void 0 ? void 0 : userSubscription.tag, plan_state: planState(userSubscription === null || userSubscription === void 0 ? void 0 : userSubscription.tag, userSubscription === null || userSubscription === void 0 ? void 0 : userSubscription.is_trial_user) });
        if (this.getAuthStore().locale) {
            const identifyEvent = new Amplitude.Identify();
            const locale = this.getAuthStore().locale;
            identifyEvent.set('app_language', locale !== null && locale !== void 0 ? locale : 'language_not_set');
        }
        Amplitude.track(eventName, eventProperties);
    }
    getAuthStore() {
        return useAuthStore();
    }
    canUseAmplitude() {
        var _a, _b;
        if (!process.env.AMPLITUDE_API_KEY) {
            return false;
        }
        if (!((_b = (_a = this.getAuthStore().user) === null || _a === void 0 ? void 0 : _a.profile) === null || _b === void 0 ? void 0 : _b.id)) {
            return false;
        }
        if (!featureFlagService.can('amplitude-kill-switch')) {
            return false;
        }
        return true;
    }
    initEngagementPlugin(lang) {
        Amplitude.add(engagementPlugin({
            serverZone: 'EU',
            locale: lang || 'lt',
        }));
    }
    initSessionReplayPlugin() {
        Amplitude.add(sessionReplayPlugin());
    }
}
export const amplitudeService = new AmplitudeService();
